import * as React from 'react';
import { ChevronLeft, ChevronRight, Minus, Plus } from 'lucide-react';
import { Button, Typography } from '@/components/common';
import { RequiredField, SignDocumentDetails } from '@/interfaces';
import { PdfFormField } from '@/lib/matchPdfFormFields';
import {
	useClientSignSession,
	ClientSignSession,
} from './hooks/useClientSignSession';
import {
	ClientSignPdfCanvas,
	ClientSignPdfCanvasHandle,
} from './ClientSignPdfCanvas';
import { SignaturePadModal } from './SignaturePadModal';
import { MismatchBanner } from './MismatchBanner';
import { SubmitAnywayPopover } from './SubmitAnywayPopover';
import { DeclineConfirmCard } from './DeclineConfirmCard';
import { useFocusTrap } from './hooks/useFocusTrap';
import { useDragToPan } from '@/hooks/useDragToPan';
import clsx from 'clsx';
import { ClientSignFieldProgressNav } from './ClientSignFieldProgressNav';
import { useClientSignOrderedFields } from './hooks/useClientSignOrderedFields';

const ZOOM_MIN = 0.5;
const ZOOM_MAX = 2.5;
const ZOOM_STEP = 0.1;

const clampZoom = (value: number): number =>
	Math.round(Math.min(ZOOM_MAX, Math.max(ZOOM_MIN, value)) * 10) / 10;

export interface ClientSignDocumentViewProps {
	documentUrl: string;
	applicationId: number | undefined;
	actionRequiredId: number | undefined;
	extractedFields?: PdfFormField[];
	requiredFields?: RequiredField[];
	actionDetails?: SignDocumentDetails;
	resubmitFilesCount?: number;
	onClose: () => void;
	onSubmitted?: () => void;
	refetch?: () => void;
	preview?: boolean;
	showInlineFieldProgressNav?: boolean;
	wasPreviouslyDeclined?: boolean;
}

export interface ClientSignDocumentLayoutHandle {
	cycleField: (direction: -1 | 1) => void;
}

const EMPTY_REQUIRED_FIELDS: RequiredField[] = [];

export const ClientSignDocumentLayout = React.forwardRef<
	ClientSignDocumentLayoutHandle,
	ClientSignDocumentViewProps & { session: ClientSignSession }
>(function ClientSignDocumentLayout(
	{
		documentUrl,
		preview = false,
		session,
		showInlineFieldProgressNav = true,
		wasPreviouslyDeclined = false,
	},
	ref
) {
	const [zoomScale, setZoomScale] = React.useState(1.1);
	const [zoomRangeValue, setZoomRangeValue] = React.useState(1.1);

	const applyZoom = React.useCallback((value: number) => {
		const next = clampZoom(value);
		setZoomScale(next);
		setZoomRangeValue(next);
	}, []);

	const commitZoomRange = React.useCallback(() => {
		setZoomScale(clampZoom(zoomRangeValue));
	}, [zoomRangeValue]);
	const [numPages, setNumPages] = React.useState(0);
	const [currentPageIndex, setCurrentPageIndex] = React.useState(0);

	const canvasRef = React.useRef<ClientSignPdfCanvasHandle>(null);
	const submitAnywayRef = React.useRef<HTMLDivElement>(null);
	const declineRef = React.useRef<HTMLDivElement>(null);
	const {
		containerRef: pdfViewportRef,
		canPan: canPanViewport,
		isPanning: isPanningViewport,
	} = useDragToPan();
	const viewportCursorClassName = canPanViewport
		? isPanningViewport
			? 'cursor-grabbing'
			: 'cursor-grab'
		: '';

	const {
		fields,
		setFieldValue,
		presetFields,
		detectionError,
		canvasKey,
		focusedFieldId,
		setFocusedFieldId,
		signatureFieldId,
		setSignatureFieldId,
		mismatchVisible,
		mismatchIndex,
		setMismatchIndex,
		isReverifying,
		showSubmitAnyway,
		setShowSubmitAnyway,
		showDecline,
		setShowDecline,
		isSubmitting,
		visibleMismatches,
		settingsByFieldId,
		handleFieldsDetected,
		handleDetectionError,
		retryDetection,
		handleApplySuggestion,
		handleSignatureApply,
		submitDocument,
		handleConfirm,
		handleReverify,
		zeroFieldsNotice,
		confirmEnabled,
		suggestionFocusId,
		suggestionAllowed,
	} = session;

	const { orderedFields, completedFieldCount } =
		useClientSignOrderedFields(session);

	useFocusTrap(showSubmitAnyway, submitAnywayRef, () => {
		if (!isSubmitting) setShowSubmitAnyway(false);
	});
	useFocusTrap(showDecline, declineRef, () => {
		if (!isSubmitting) setShowDecline(false);
	});

	const selectedFieldIndex = focusedFieldId
		? orderedFields.findIndex((field) => field.id === focusedFieldId)
		: -1;

	const scrollToField = (fieldId: string) => {
		canvasRef.current?.scrollToField(fieldId);
	};

	const cycleField = React.useCallback(
		(direction: -1 | 1) => {
			if (orderedFields.length === 0) return;
			const current =
				selectedFieldIndex === -1
					? direction === 1
						? -1
						: 0
					: selectedFieldIndex;
			const nextIndex =
				(current + direction + orderedFields.length) % orderedFields.length;
			const nextId = orderedFields[nextIndex].id;
			setFocusedFieldId(nextId);
			canvasRef.current?.focusField(nextId);
		},
		[orderedFields, selectedFieldIndex, setFocusedFieldId]
	);

	React.useImperativeHandle(ref, () => ({ cycleField }), [cycleField]);

	const goToMismatch = (index: number) => {
		const id = visibleMismatches[index];
		if (!id) return;
		setMismatchIndex(index);
		canvasRef.current?.scrollToField(id);
	};

	return (
		<div className="relative flex flex-col h-full min-h-0">
			<style jsx global>{`
				.client-sign-zoom-range {
					-webkit-appearance: none;
					appearance: none;
					height: 12px;
					background: repeating-linear-gradient(
						to right,
						#c9c5cc 0 3px,
						transparent 3px 7px
					);
					background-position: center;
					background-repeat: no-repeat;
					background-size: 100% 2px;
				}
				.client-sign-zoom-range::-webkit-slider-thumb {
					-webkit-appearance: none;
					appearance: none;
					width: 12px;
					height: 12px;
					background: #40bb71;
					border: none;
					border-radius: 50%;
					cursor: pointer;
				}
				.client-sign-zoom-range::-moz-range-thumb {
					width: 12px;
					height: 12px;
					background: #40bb71;
					border: none;
					border-radius: 50%;
					cursor: pointer;
				}
				.client-sign-choice option {
					color: #0d0616;
					background-color: #fff;
				}
			`}</style>
			{showInlineFieldProgressNav ? (
				<div className="flex shrink-0 justify-end pb-3">
					<ClientSignFieldProgressNav
						completed={completedFieldCount}
						total={orderedFields.length}
						onPrevious={() => cycleField(-1)}
						onNext={() => cycleField(1)}
					/>
				</div>
			) : null}

			<div
				ref={pdfViewportRef}
				className={`flex-1 min-h-0 overflow-auto scroll-custom bg-grey-5 rounded-lg p-4 ${viewportCursorClassName}`}
			>
				{detectionError && (
					<div className="flex flex-col items-center justify-center gap-3 py-16">
						<Typography type="regular-14" className="text-main-red">
							We couldn’t read the fields in this document. Please try again.
						</Typography>
						<Button
							type="button"
							decoration="primary"
							size="fit"
							label="Retry"
							onClick={retryDetection}
						/>
					</div>
				)}
				{documentUrl && !detectionError && (
					<ClientSignPdfCanvas
						key={canvasKey}
						ref={canvasRef}
						pdfUrl={documentUrl}
						fields={fields}
						onFieldValueChange={setFieldValue}
						onSignatureClick={setSignatureFieldId}
						presetFields={presetFields}
						onFieldsDetected={handleFieldsDetected}
						onDetectionError={handleDetectionError}
						zoomScale={zoomScale}
						onNumPagesChange={setNumPages}
						onCurrentPageChange={setCurrentPageIndex}
						mismatchedFieldIds={visibleMismatches}
						focusedFieldId={suggestionAllowed ? suggestionFocusId : null}
						settingsByFieldId={settingsByFieldId}
						onApplySuggestion={handleApplySuggestion}
						onFieldFocus={setFocusedFieldId}
						onFieldBlur={(fieldId) =>
							setFocusedFieldId((current) =>
								current === fieldId ? null : current
							)
						}
					/>
				)}
			</div>

			{zeroFieldsNotice && (
				<div className="mt-3 rounded-lg bg-grey-5 px-4 py-3">
					<Typography type="regular-14">
						No fillable fields were found. You can still confirm and submit this
						document.
					</Typography>
				</div>
			)}

			{mismatchVisible && visibleMismatches.length > 0 && (
				<div className="mt-3">
					<MismatchBanner
						currentIndex={Math.min(
							mismatchIndex,
							Math.max(visibleMismatches.length - 1, 0)
						)}
						total={visibleMismatches.length}
						isReverifying={isReverifying}
						onReverify={() => handleReverify(scrollToField)}
						onPrev={() => goToMismatch(mismatchIndex - 1)}
						onNext={() => goToMismatch(mismatchIndex + 1)}
					/>
				</div>
			)}

			<div className="grid grid-cols-[minmax(0,1fr)_auto_minmax(0,1fr)] items-center gap-3 pt-4 shrink-0">
				<div className="justify-self-start">
					{!wasPreviouslyDeclined && (
						<Button
							type="button"
							decoration="none"
							size="fit"
							label="Decline Document"
							disabled={isSubmitting}
							className="!h-12 text-[#7E7E7E]"
							onClick={() => setShowDecline(true)}
						/>
					)}
				</div>
				<div className="flex items-center gap-5">
					<Typography type="regular-14" className="text-gray-35">
						Page {numPages === 0 ? 0 : currentPageIndex + 1} of {numPages}
					</Typography>
					<button
						type="button"
						aria-label="Previous page"
						disabled={currentPageIndex <= 0}
						onClick={() =>
							canvasRef.current?.scrollToPage(currentPageIndex - 1)
						}
						className="flex size-7 items-center justify-center rounded disabled:opacity-30 hover:bg-grey-5"
					>
						<ChevronLeft className="size-5" />
					</button>
					<button
						type="button"
						aria-label="Next page"
						disabled={currentPageIndex >= numPages - 1}
						onClick={() =>
							canvasRef.current?.scrollToPage(currentPageIndex + 1)
						}
						className="flex size-7 items-center justify-center rounded disabled:opacity-30 hover:bg-grey-5"
					>
						<ChevronRight className="size-5" />
					</button>
					<div className="flex max-h-10 max-w-[192px] items-center gap-2 rounded-3xl border border-[#0D061614] bg-gray-5 p-2 shadow-shadow2">
						<button
							type="button"
							aria-label="Zoom out"
							disabled={zoomScale <= ZOOM_MIN}
							onClick={() => applyZoom(zoomScale - ZOOM_STEP)}
							className="flex h-8 w-8 items-center justify-center rounded hover:bg-gray-100 disabled:opacity-30"
						>
							<Minus className="size-4 text-dark-100" />
						</button>
						<input
							type="range"
							min={ZOOM_MIN}
							max={ZOOM_MAX}
							step={ZOOM_STEP}
							value={zoomRangeValue}
							aria-label="Zoom"
							onChange={(event) =>
								setZoomRangeValue(clampZoom(parseFloat(event.target.value)))
							}
							onPointerUp={commitZoomRange}
							onBlur={commitZoomRange}
							onKeyUp={(event) => {
								if (event.key === 'Enter') commitZoomRange();
							}}
							className="client-sign-zoom-range w-24 cursor-pointer"
						/>
						<button
							type="button"
							aria-label="Zoom in"
							disabled={zoomScale >= ZOOM_MAX}
							onClick={() => applyZoom(zoomScale + ZOOM_STEP)}
							className="flex h-8 w-8 items-center justify-center rounded hover:bg-gray-100 disabled:opacity-30"
						>
							<Plus className="size-4 text-dark-100" />
						</button>
					</div>
				</div>
				<div className="justify-self-end">
					<Button
						type="button"
						decoration={mismatchVisible ? 'secondary' : 'primary'}
						size="fit"
						label={
							isSubmitting
								? 'Submitting…'
								: mismatchVisible
								? 'Submit Anyway'
								: 'Confirm & Submit'
						}
						disabled={mismatchVisible ? isSubmitting : !confirmEnabled}
						loading={isSubmitting}
						className={clsx(
							'!h-12 disabled:!bg-[#0D06160F] disabled:!text-[#0D06164D] disabled:!border-none',
							{
								'pointer-events-none': preview,
							}
						)}
						onClick={() =>
							mismatchVisible
								? setShowSubmitAnyway(true)
								: handleConfirm(scrollToField)
						}
					/>
				</div>
			</div>

			<SignaturePadModal
				isOpen={signatureFieldId !== null}
				onClose={() => setSignatureFieldId(null)}
				onApply={handleSignatureApply}
			/>

			{showSubmitAnyway && (
				<div className="absolute inset-0 z-30 flex items-end justify-end bg-dark-100/20 p-4">
					<div ref={submitAnywayRef}>
						<SubmitAnywayPopover
							isSubmitting={isSubmitting}
							onBack={() => setShowSubmitAnyway(false)}
							onSubmit={() => void submitDocument(true)}
						/>
					</div>
				</div>
			)}

			{showDecline && (
				<div className="absolute inset-0 z-30 flex items-end justify-start bg-dark-100/20 p-4">
					<div ref={declineRef}>
						<DeclineConfirmCard
							isSubmitting={isSubmitting}
							onCancel={() => setShowDecline(false)}
							onConfirm={() => void submitDocument(false)}
							confirmInert={preview}
						/>
					</div>
				</div>
			)}
		</div>
	);
});

export const ClientSignDocumentView: React.FC<ClientSignDocumentViewProps> = ({
	applicationId,
	actionRequiredId,
	extractedFields,
	requiredFields = EMPTY_REQUIRED_FIELDS,
	onClose,
	onSubmitted,
	refetch,
	preview = false,
	...rest
}) => {
	const session = useClientSignSession({
		applicationId,
		actionRequiredId,
		extractedFields,
		requiredFields,
		onClose,
		onSubmitted,
		refetch,
		preview,
	});

	return (
		<ClientSignDocumentLayout
			{...rest}
			applicationId={applicationId}
			actionRequiredId={actionRequiredId}
			extractedFields={extractedFields}
			requiredFields={requiredFields}
			onClose={onClose}
			onSubmitted={onSubmitted}
			refetch={refetch}
			preview={preview}
			session={session}
		/>
	);
};

export default ClientSignDocumentView;
